import "./style.scss";

import * as React from "react";
import { Button, Checkbox, Dialog, DialogContent, DialogTitle, Input, InputLabel, Menu, MenuItem, Typography, Icon as IconMaterial } from "@mui/material";
import { Icon } from "../../icon/icon";
import { t } from "i18next";

export interface ISearchListSelector {
    isAllChecked: boolean;
    selectAction: (active: boolean, limit?: number) => void;
}

interface ISearchListSelectorState {
    selectorLimit: string;
    selectorLimitErrorMessage: string | undefined;
    selectorLimitPopupOpened: boolean;
    selectorStrategyMenu: HTMLElement | undefined;
}

export class SearchListSelector extends React.Component<ISearchListSelector, ISearchListSelectorState> {

    constructor(props: ISearchListSelector) {
        super(props);

        this.state = {
            selectorLimit: "",
            selectorLimitErrorMessage: undefined,
            selectorLimitPopupOpened: false,
            selectorStrategyMenu: undefined,
        };
    }

    private get isSelectorLimitValid(): boolean {
        return this.state.selectorLimit !== "" && this.state.selectorLimitErrorMessage === undefined;
    }

    public render() {
        return <div className={"search-selector"}>
            <Checkbox onChange={(_event: React.ChangeEvent<{}>, checked: boolean) => this.props.selectAction(checked)}
                      checked={this.props.isAllChecked}
                      data-testid="list-select-all"
                      color="primary" />
            <IconMaterial data-testid="search-list-strategy-icon"
                          className={"search-selector__strategy-selector"}
                          onClick={(event: any) => this.openSelectorStrategyMenu(event.currentTarget)}
            >
                keyboard_arrow_down
            </IconMaterial>
            <Menu id="search-list-strategy-menu"
                  anchorEl={this.state.selectorStrategyMenu}
                  open={Boolean(this.state.selectorStrategyMenu)}
                  onClose={() => this.hideSelectorStrategyMenu()}
            >
                <MenuItem data-testid="search-list-strategy-all" onClick={() => this.selectAllAndHideMenu()}>
                    {t("components.searchList.selectorStrategyAll")}
                </MenuItem>
                <MenuItem data-testid="search-list-strategy-custom" onClick={() => this.openSelectorLimitPopup()}>
                    {t("components.searchList.selectorStrategyCustom")}
                </MenuItem>
            </Menu>
            <Dialog open={this.state.selectorLimitPopupOpened} className={"search-selector-popup"} data-testid={"search-selector-popup"}>
                <form onSubmit={async event => await this.executeAction(event)}>
                    <div className={"search-selector-popup__container"}>
                        <div className="search-selector-popup__close">
                            <Icon icon="close" onClick={() => this.closeSelectorLimitPopup()} color="primary" />
                        </div>
                        <DialogTitle className={"search-selector-popup__title"}>
                            {t("components.searchList.customSelector.title")}
                        </DialogTitle>

                        <DialogContent className={"search-selector-popup__content"}>
                            <Typography className={"search-selector-popup__description"}>
                                {t("components.searchList.customSelector.description")}
                            </Typography>
                            <InputLabel htmlFor="quantity" className={"search-selector-popup__input-label"}>
                                <Typography className={"search-selector-popup__input-text"}>{t("components.searchList.customSelector.quantity")}</Typography>
                                <Input
                                    autoFocus={true}
                                    data-testid="search-list-custom-quantity"
                                    key={"select-quantity"}
                                    className={"search-selector-popup__input"}
                                    id={"select-quantity"}
                                    onChange={(e: any) => this.setSelectorLimit(e.target.value)}
                                    value={this.state.selectorLimit}
                                />
                                {this.state.selectorLimitErrorMessage && <Typography className={"search-selector-popup__error-message"} data-testid={"search-list-custom-error"}>
                                    {this.state.selectorLimitErrorMessage}
                                </Typography>}
                            </InputLabel>
                        </DialogContent>
                        <div className={"search-selector-popup__submit-container"}>
                            <Button
                                    variant="contained"
                                    className={"search-selector-popup__action-button search-selector-popup__action-button--cancel"}
                                    data-testid="search-list-custom-cancel"
                                    onClick={() => this.closeSelectorLimitPopup()}
                            >
                                {t(`components.popin.cancel`)}
                            </Button>
                            <Button
                                data-testid="search-list-custom-action"
                                color={"primary"}
                                variant={"contained"}
                                type={"submit"}
                                disabled={!this.isSelectorLimitValid}
                                className={"search-selector-popup__action-button search-selector-popup__action-button--submit"}
                                onClick={(event: React.SyntheticEvent<HTMLElement>) => this.executeAction(event)}
                            >
                                {t(`components.popin.ok`)}
                            </Button>
                        </div>
                    </div>
                </form>
            </Dialog>
        </div>;
    }

    private openSelectorStrategyMenu(element: HTMLElement) {
        this.setState(prevState => ({...prevState, selectorStrategyMenu: element}));
    }

    private hideSelectorStrategyMenu() {
        this.setState(prevState => (
            {...prevState,
                selectorStrategyMenu: undefined,
                selectorLimit: "",
                selectorLimitErrorMessage: undefined,
            }));
    }

    private selectAllAndHideMenu() {
        this.props.selectAction(true);
        this.hideSelectorStrategyMenu();
    }

    private openSelectorLimitPopup() {
        this.setState(prevState => ({...prevState, selectorLimitPopupOpened: true}));
    }

    private closeSelectorLimitPopup() {
        this.setState(prevState => ({...prevState, selectorLimitPopupOpened: false}));
        this.hideSelectorStrategyMenu();
    }

    private setSelectorLimit(limit: string) {
        this.setState(prevState => ({...prevState, selectorLimit: limit}));

        if (limit === "") {
            return;
        }

        const numberLimit = Number(limit);
        if (limit !== numberLimit.toString()) {
            this.setState(prevState => ({...prevState, selectorLimitErrorMessage: t("components.searchList.customSelector.error.invalidNumber")}));
            return;
        }
        if (numberLimit <= 0) {
            this.setState(prevState => ({...prevState, selectorLimitErrorMessage: t("components.searchList.customSelector.error.nonPositiveValue")}));
            return;
        }

        this.setState(prevState => ({...prevState, selectorLimitErrorMessage: undefined}));
    }

    private executeAction(event: React.SyntheticEvent<{}>) {
        event.preventDefault();

        if (!this.isSelectorLimitValid) {
            return;
        }

        this.props.selectAction(true, Number(this.state.selectorLimit));
        this.closeSelectorLimitPopup();
    }
}
